import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { quotationInvoiceDocumentTypeDisplayName } from "@/modules/quotation-invoice/domain/document-type";
import type { QuotationInvoiceDocumentListItem } from "@/modules/quotation-invoice/domain/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  DataTable,
  confirm,
  createSortableColumn,
  toast,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  DollarSign,
  EditIcon,
  EyeIcon,
  FileText,
  Receipt,
  TrashIcon,
  XCircleIcon,
} from "lucide-react";
import {
  useDeleteQuotationInvoiceDocument,
  useUpdateQuotationInvoiceDocumentStatus,
} from "../api/queries";
import { useQuotationIssueWithPrecheck } from "../lib/useQuotationIssueWithPrecheck";
import { formatDocumentCurrency } from "../lib/format-document-currency";
import { DocumentStatusBadge } from "./DocumentStatusBadge";
import { IssueQuotationStockPrecheckDialog } from "./IssueQuotationStockPrecheckDialog";

type DocumentRow = QuotationInvoiceDocumentListItem;

const getTypeIcon = (documentType: string) => {
  switch (documentType) {
    case "quotation":
      return <DollarSign className="h-4 w-4 text-blue-500" />;
    case "invoice":
      return <FileText className="h-4 w-4 text-amber-500" />;
    case "receipt":
      return <Receipt className="h-4 w-4 text-emerald-500" />;
    default:
      return <FileText className="h-4 w-4 text-muted-foreground" />;
  }
};

const getTypeLabel = (
  documentType: string,
  documentTypeName?: string | null,
) => {
  if (documentTypeName?.trim()) return documentTypeName;
  return quotationInvoiceDocumentTypeDisplayName(documentType);
};

type DocumentsTableProps = {
  data: DocumentRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (row: DocumentRow) => void;
  onEdit?: (row: DocumentRow) => void;
};

export function DocumentsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
}: DocumentsTableProps) {
  const { has } = usePermissions();
  const updateStatus = useUpdateQuotationInvoiceDocumentStatus();
  const deleteDocument = useDeleteQuotationInvoiceDocument();
  const issuePrecheck = useQuotationIssueWithPrecheck();

  const columns: TanstackReactTable.ColumnDef<DocumentRow>[] = [
    createSortableColumn<DocumentRow>("documentNumber", "ເລກທີເອກະສານ", {
      size: 140,
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => onView(row.original)}
          className="font-medium text-primary hover:underline"
        >
          #{row.original.documentNumber ?? row.original.id.slice(0, 8)}
        </button>
      ),
    }),
    createSortableColumn<DocumentRow>("documentType", "ປະເພດ", {
      size: 120,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {getTypeIcon(row.original.documentType)}
          <span className="text-sm">
            {getTypeLabel(
              row.original.documentType,
              row.original.documentTypeName,
            )}
          </span>
        </div>
      ),
    }),
    {
      id: "customerName",
      header: "ລູກຄ້າ",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.original.customerName}</span>
          {row.original.customerContact && (
            <span className="text-muted-foreground text-xs">
              {row.original.customerContact}
            </span>
          )}
        </div>
      ),
      size: 200,
    },
    {
      id: "createdByName",
      header: "ຜູ້ອອກ",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.createdByName?.trim() || "—"}
        </span>
      ),
      size: 140,
    },
    createSortableColumn<DocumentRow>("issueDate", "ວັນທີ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.issueDate
            ? formatDateLocal(row.original.issueDate)
            : "—"}
        </span>
      ),
    }),
    createSortableColumn<DocumentRow>("total", "ຈຳນວນເງິນ", {
      size: 120,
      cell: ({ row }) => (
        <span className="font-medium">
          {formatDocumentCurrency(row.original.total, row.original.currency)}
        </span>
      ),
    }),
    createSortableColumn<DocumentRow>("status", "ສະຖານະ", {
      size: 110,
      cell: ({ row }) => <DocumentStatusBadge status={row.original.status} />,
    }),
    {
      size: 80,
      id: "actions",
      cell: ({ row }: { row: { original: DocumentRow } }) => {
        const doc = row.original;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          onClick: () => void;
          variant?: "destructive" | "secondary" | "outline";
        }[] = [];

        if (has(Permissions.quotation_invoice.read)) {
          actions.push({
            label: "ເບິ່ງ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(doc),
          });
        }

        if (
          doc.status === "draft" &&
          has(Permissions.quotation_invoice.update) &&
          onEdit
        ) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            variant: "outline",
            onClick: () => onEdit(doc),
          });
        }

        if (
          doc.status === "draft" &&
          has(Permissions.quotation_invoice.update)
        ) {
          actions.push({
            label: "ອອກເອກະສານ",
            icon: <FileText className="h-4 w-4" />,
            onClick: () => {
              issuePrecheck.requestIssue({
                id: doc.id,
                documentNumber: doc.documentNumber ?? doc.id.slice(0, 8),
                documentType: doc.documentType,
                onIssue: () =>
                  updateStatus.mutate({ id: doc.id, status: "issued" }),
              });
            },
          });
        }

        if (
          doc.status !== "cancelled" &&
          has(Permissions.quotation_invoice.update)
        ) {
          actions.push({
            label: "ຍົກເລີກ",
            icon: <XCircleIcon className="h-4 w-4" />,
            variant: "destructive",
            onClick: async () => {
              const ok = await confirm({
                title: "ຍົກເລີກເອກະສານ",
                description: `ຢືນຢັນຍົກເລີກເອກະສານ #${doc.documentNumber ?? doc.id.slice(0, 8)}? ການດຳເນີນການນີ້ມັກຈະບໍ່ຍົກຄືນ.`,
                actionText: "ຍົກເລີກ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) updateStatus.mutate({ id: doc.id, status: "cancelled" });
            },
          });
        }

        if (
          (doc.status === "draft" || doc.status === "cancelled") &&
          has(Permissions.quotation_invoice.delete)
        ) {
          actions.push({
            label: "ລຶບ",
            icon: <TrashIcon className="h-4 w-4" />,
            variant: "destructive",
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບເອກະສານ",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບເອກະສານ #${doc.documentNumber ?? doc.id.slice(0, 8)}?`,
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok)
                toast.promise(deleteDocument.mutateAsync(doc.id), {
                  loading: "ກຳລັງລຶບ...",
                  success: "ລຶບສຳເລັດ",
                  error: "ລຶບລົ້ມເຫຼວ",
                });
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <>
      <DataTable<DocumentRow, unknown>
        noDataMessage="ບໍ່ພົບເອກະສານ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
      <IssueQuotationStockPrecheckDialog {...issuePrecheck.precheckDialogProps} />
    </>
  );
}
